import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, Loader2, Lock } from "lucide-react";
import { adminLogin, adminMe, getToken } from "../lib/api";

function formatError(detail) {
  if (detail == null) return "Anmeldung fehlgeschlagen. Bitte erneut versuchen.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (getToken()) {
      adminMe().then(() => navigate("/admin", { replace: true })).catch(() => {});
    }
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await adminLogin(form.email, form.password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-blue-950 px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
        className="w-full max-w-md rounded-3xl bg-white p-8 sm:p-10 shadow-[0_50px_100px_-40px_rgba(0,0,0,0.5)]"
      >
        <div className="flex items-center gap-2 mb-8">
          <span className="h-10 w-10 rounded-full bg-blue-700 grid place-items-center"><Zap className="h-5 w-5 text-white" strokeWidth={2.5} /></span>
          <span className="font-heading font-semibold text-navy-900 text-lg">Energie·Tarifberater</span>
        </div>
        <p className="text-xs uppercase tracking-[0.25em] text-blue-700 mb-2 inline-flex items-center gap-2"><Lock className="h-3.5 w-3.5" /> Interner Bereich</p>
        <h1 className="text-3xl font-heading font-semibold text-navy-900 mb-8">Lead-Dashboard</h1>

        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <input
            required type="email" placeholder="E-Mail"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-slate-50 rounded-xl border border-slate-200 focus:border-blue-500 outline-none px-4 py-3.5 text-sm transition-colors"
            data-testid="admin-email-input"
          />
          <input
            required type="password" placeholder="Passwort"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full bg-slate-50 rounded-xl border border-slate-200 focus:border-blue-500 outline-none px-4 py-3.5 text-sm transition-colors"
            data-testid="admin-password-input"
          />
          {error && <p className="text-sm text-rose-600" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit" disabled={loading}
            className="w-full rounded-xl bg-blue-700 hover:bg-blue-600 disabled:opacity-60 text-white py-3.5 font-medium transition-colors flex items-center justify-center gap-2"
            data-testid="admin-login-submit"
          >
            {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Anmelden…</> : "Anmelden"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
